import { useState } from "react";
import { Link } from "react-router-dom";
import { type IconType } from "react-icons";
import { FaChevronDown } from "react-icons/fa6";
import { footerConfig } from "./footerConfig";

interface FooterLink {
  label: string;
  to: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  icon: IconType;
  to: string;
}

interface ContactInfo {
  email?: string;
  phone?: string;
  address?: string;
}

export interface FooterConfig {
  logo?: string;
  description?: string;
  sections?: FooterSection[];
  social?: SocialLink[];
  contact?: ContactInfo;
  copyright?: string;
}

export function Footer({ config = footerConfig }: { config?: FooterConfig }) {
  return (
    <footer className="backdrop-blur-md bg-white/20 dark:bg-gray-900 dark:text-gray-200 pt-12 pb-6 px-4 md:px-8 lg:px-16 rounded-xl">
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10">

        {/* Logo + Description */}
        <div>
          <img src={config?.logo} alt="logo" className="w-32 mb-4 rounded-2xl" />
          <p className="text-sm leading-relaxed mb-5 text-gray-700 dark:text-gray-300">
            {config?.description}
          </p>

          {/* Social Icons */}
          <div className="flex items-center gap-4">
            {config?.social?.map((s, index) => (
              <Link
                key={index}
                to={s.to}
                className="text-gray-600 dark:text-gray-300 hover:text-blue-500"
              >
                <s.icon size={22} />
              </Link>
            ))}
          </div>
        </div>

        {/* Dynamic Sections (mobile collapsible, desktop expanded) */}
        {config?.sections?.map((section, i) => (
          <FooterCollapsible key={i} section={section} />
        ))}

        {/* Contact Section */}
        <FooterCollapsible
          section={{
            title: "Contact Us",
            links: [
              config?.contact?.email
                ? { label: `Email: ${config?.contact?.email}`, to: "#" }
                : null,
              config?.contact?.phone
                ? { label: `Phone: ${config?.contact?.phone}`, to: "#" }
                : null,
              config?.contact?.address
                ? { label: config?.contact?.address, to: "#" }
                : null
            ].filter(Boolean) as FooterLink[]
          }}
        />
      </div>

      {/* Copyright */}
      <div className="text-center text-gray-500 dark:text-gray-400 text-xs mt-10 pt-5 border-t border-gray-300 dark:border-gray-700">
        {config?.copyright}
      </div>
    </footer>
  );
}

interface CollapsibleProps {
  section: FooterSection;
}

function FooterCollapsible({ section }: CollapsibleProps) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      {/* Desktop = always open */}
      <div className="hidden md:block">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-200 mb-4 border-b border-gray-300 dark:border-gray-700 pb-1">
          {section.title}
        </h3>
        <ul className="space-y-2">
          {section.links.map((link, idx) => (
            <li key={idx}>
              <Link
                to={link.to}
                className="hover:text-blue-500 text-gray-700 dark:text-gray-300 text-sm"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Mobile collapsible */}
      <div className="md:hidden border-b border-gray-300 dark:border-gray-700 py-3">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex justify-between items-center text-gray-900 dark:text-gray-200 font-semibold"
        >
          <span>{section.title}</span>
          <FaChevronDown
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <ul className="mt-3 space-y-2">
            {section.links.map((link, idx) => (
              <li key={idx}>
                <Link
                  to={link.to}
                  className="block text-gray-700 dark:text-gray-300 hover:text-blue-500 text-sm"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}